import { useCallback } from 'react';
import { useRefreshOnPortalMessage } from './useRefreshOnPortalMessage';
import { useEncaissementStore } from '../stores/encaissementStore';
import { tasksService } from '../services/tasksService';
import { usePartialSync } from './usePartialSync';
import { useSyncStore } from '../stores/syncStore';

export const PAYMENT_COMPLETED_MESSAGE = 'portal:payment-completed';

/**
 * The host names only a customer, so a completion is attributed to the relance the operator opened
 * the receipt window from; a receipt for anyone else is ignored.
 */
export const useRecordEncaissement = (): void => {
    const { trigger: triggerPartialSync, canSync } = usePartialSync();

    const handlePaymentCompleted = useCallback((message: Record<string, unknown>) => {
        const { customerId, amount } = message;
        if (typeof customerId !== 'number' || !Number.isFinite(customerId)) return;
        if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return;

        const pending = useEncaissementStore.getState().pendingByCustomer;
        if (!(customerId in pending)) return;
        const relanceId = pending[customerId];

        const syncAfterwards = () => {
            const { syncing, initialSyncRequired } = useSyncStore.getState();
            if (canSync && !syncing && !initialSyncRequired) void triggerPartialSync();
        };
        // An expected relance has nothing to record against; the sync alone updates the forecast.
        if (relanceId === null) { syncAfterwards(); return; }

        // The money moved in AcombaGO regardless, so a failed write is not the operator's problem.
        void tasksService.recordPayment(relanceId, amount)
            .catch(() => undefined)
            .then(syncAfterwards);
    }, [canSync, triggerPartialSync]);

    useRefreshOnPortalMessage(PAYMENT_COMPLETED_MESSAGE, handlePaymentCompleted);
};
